import { useCallback, useEffect, useState } from "react";
import { Loader2, Search, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { AvisoHomonimo, ComparativoIdentidad, ConstanciaButton, DatosRegistro, DictamenRegistrado } from "./FichaPartes";
import { DictamenForm } from "./DictamenForm";

const ESTATUS = { pendiente: "Pendiente", confirmada: "Confirmada", descartada_falso_positivo: "Falso positivo",
  descartada: "Descartada (umbral)", anulada_por_reversion: "Versión revertida" };

function Encabezado({ f }) {
  const m = f.match;
  return (
    <div data-testid="ficha-encabezado" className="flex flex-wrap items-center gap-2 text-xs">
      <span className={`rounded-full border px-2 py-0.5 font-semibold ${m.critica ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
        {m.critica ? "Crítica · decide el OC" : "No crítica"}
      </span>
      <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-600" data-testid="ficha-estatus">
        {ESTATUS[m.estatus] || m.estatus}
      </span>
      <span className="text-slate-500">{f.persona?.tipo}: <b className="text-slate-800">{f.persona?.nombre}</b></span>
      <span className="text-slate-500">Similitud <b className={m.score >= 90 ? "text-red-600" : "text-amber-600"}>{m.score}%</b></span>
    </div>
  );
}

export function FichaCoincidencia({ matchId, onDecided }) {
  const [ficha, setFicha] = useState(null);
  const [error, setError] = useState("");
  const cargar = useCallback(async () => {
    setError("");
    try { setFicha((await api.get(`/screening/matches/${matchId}/ficha`)).data); }
    catch (e) { setError(apiError(e)); }
  }, [matchId]);
  useEffect(() => { cargar(); }, [cargar]);

  if (error) return <p data-testid="ficha-error" className="text-sm text-red-600">{error}</p>;
  if (!ficha) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const alDecidir = async (res) => { await cargar(); onDecided?.(res); };
  return (
    <div data-testid="ficha-coincidencia" className="space-y-3">
      <Encabezado f={ficha} />
      {ficha.aviso_homonimo && <AvisoHomonimo texto={ficha.aviso_texto} />}
      <DatosRegistro registro={ficha.registro} />
      <ComparativoIdentidad filas={ficha.comparativo} tipoPersona={ficha.persona?.tipo} origen={ficha.comparativo_origen} />
      {ficha.match.sello && <p className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-500">{ficha.match.sello}</p>}
      {ficha.pendiente ? <DictamenForm ficha={ficha} onDecided={alDecidir} />
        : ficha.dictamen && <DictamenRegistrado dictamen={ficha.dictamen} />}
      {ficha.persona_bloqueada && !ficha.pendiente && (
        <p data-testid="ficha-bloqueo-vigente" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          La persona sigue con bloqueo por sanción: se levanta con el desbloqueo con carta (Perfil 360 → Listas).
        </p>
      )}
      {ficha.constancia_disponible && (
        <div className="flex justify-end">
          <ConstanciaButton matchId={ficha.match.id} folio={ficha.constancia?.folio || ficha.match.constancia_folio}
            testid="ficha-constancia-btn" />
        </div>
      )}
    </div>
  );
}

export function FichaCoincidenciaDialog({ matchId, titulo, open, onOpenChange, onDecided }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="ficha-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Search className="h-5 w-5 text-primary" /> Coincidencia{titulo ? ` — ${titulo}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">Detalle de la coincidencia, comparativo de identidad y dictamen</DialogDescription>
        </DialogHeader>
        {matchId && <FichaCoincidencia matchId={matchId} onDecided={onDecided} />}
      </DialogContent>
    </Dialog>
  );
}

export function VerFichaButton({ match, onDecided }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" size="sm" variant="outline" data-testid={`ver-ficha-${match.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="h-8 gap-1.5 px-2.5">
        <Search className="h-3.5 w-3.5" /> Ver ficha
      </Button>
      <FichaCoincidenciaDialog matchId={open ? match.id : null} titulo={match.persona_nombre} open={open}
        onOpenChange={setOpen} onDecided={onDecided} />
    </>
  );
}

export function CoincidenciasProspecto({ prospectoId, onDecided }) {
  const [rows, setRows] = useState(null);
  const cargar = useCallback(() => {
    api.get(`/screening/matches/prospecto/${prospectoId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [prospectoId]);
  useEffect(() => { cargar(); }, [cargar]);
  if (!rows?.length) return null;
  return (
    <div data-testid="prospecto-coincidencias" className="rounded-md border border-slate-200 bg-white p-2.5">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Coincidencias en listas ({rows.length})</p>
      <ul className="space-y-2">
        {rows.map((m) => (
          <li key={m.id} data-testid={`prospecto-coincidencia-${m.id}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
            <div className="min-w-0 text-xs">
              <p className="font-semibold text-slate-800">{m.lista_legible}</p>
              <p className="text-slate-500">{m.entrada_nombre || "—"} · {m.score}% · {ESTATUS[m.estatus] || m.estatus}</p>
            </div>
            <div className="flex gap-1.5">
              <VerFichaButton match={m} onDecided={() => { cargar(); onDecided?.(); }} />
              {m.estatus !== "pendiente" && m.estatus !== "anulada_por_reversion" && (
                <ConstanciaButton matchId={m.id} folio={m.constancia_folio} compact />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
